import type { FC } from 'react';
import { Col, Form, type FormInstance, Row } from 'antd';

import FormInput from '@/views/components/Inputs/FormInput';
import FormTextArea from '@/views/components/Inputs/FormTextArea';
import HiddenFormInput from '@/views/components/Inputs/HiddenFormInput';
import PostalCodeInput, { type PostalCodeInputValues } from '@/views/components/Selectors/PostalCodeInput';
import { CitySelect, CountrySelect, StateSelect } from '@/views/screens/common';

import type { CarrierInformationSettings } from '../../../types';

interface ClaimsSettingsCarrierAddressFormProps {
  addressType: 'ldClaimAddress' | 'ocClaimAddress';
  form: FormInstance<CarrierInformationSettings>;
}

const ClaimsSettingsCarrierAddressForm: FC<ClaimsSettingsCarrierAddressFormProps> = ({ addressType, form }) => {
  const countryCode = Form.useWatch([addressType, 'carrierDetailCountry'], form);
  const stateCode = Form.useWatch([addressType, 'carrierDetailState'], form);
  const handleZipDetails = (data: PostalCodeInputValues) => {
    form.setFieldValue([addressType, 'carrierDetailCity'], data?.city);
    form.setFieldValue([addressType, 'carrierDetailState'], data?.state);
    form.setFieldValue([addressType, 'carrierDetailZip'], data?.zip);
    form.setFieldValue([addressType, 'carrierDetailCountry'], data?.countryCode);
  };

  return (
    <Row gutter={15}>
      <Col md={12}>
        <FormInput editMode name={[addressType, 'carrierDetailName']} label="Company Name" margin="mb-10" />
        {/* @ts-ignore */}
        <FormTextArea editMode name={[addressType, 'carrierDetailAddr1']} label="Address" margin="mb-10" rows={3} />
        <CountrySelect label="Country" editMode name={[addressType, 'carrierDetailCountry']} margin="mb-10" />
        <PostalCodeInput
          editMode
          countryCode={countryCode}
          // @ts-expect-error
          name={[addressType, 'carrierDetailZip']}
          label="Postal Code"
          placeholder="Enter postal code"
          margin="mb-10"
          handleZipDetails={handleZipDetails}
        />
      </Col>
      <Col md={12}>
        <StateSelect
          editMode
          name={[addressType, 'carrierDetailState']}
          margin="mb-10"
          label="State"
          countryCode={countryCode}
        />
        <CitySelect
          editMode
          name={[addressType, 'carrierDetailCity']}
          margin="mb-40"
          countryCode={countryCode}
          stateCode={stateCode}
        />
        <FormInput
          editMode
          name={[addressType, 'carrierEmail']}
          label="Contact Email Address"
          margin="mb-10"
          validateTrigger={['onChange', 'onBlur']}
          rules={[
            {
              message: 'The input is not valid E-mail!',
              type: 'email',
            },
          ]}
        />
        <FormInput editMode name={[addressType, 'carrierDetailPhone']} label="Contact Number" margin="mb-10" />
      </Col>

      <HiddenFormInput name={[addressType, 'addressType']} />
      <HiddenFormInput name={[addressType, 'carrierDetailAddr2']} />
      <HiddenFormInput name={[addressType, 'carrierDetailFax']} />
      <HiddenFormInput name={[addressType, 'carrierDetailKey']} />
      <HiddenFormInput name={[addressType, 'carrierKey']} />
      <HiddenFormInput name={[addressType, 'dirtyFlag']} />
      <HiddenFormInput name={[addressType, 'carrierDetailPhone']} />
    </Row>
  );
};

export default ClaimsSettingsCarrierAddressForm;
